import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'
import EventHeader from '../components/EventDetail/EventHeader'
import EventInfo from '../components/EventDetail/EventInfo'
import SocialSection from '../components/EventDetail/SocialSection'
import MediaSection from '../components/EventDetail/MediaSection'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import StatusMessage from '../components/UI/StatusMessage'
import ArtistEditModal from '../components/ArtistEditModal'
import FlyerUpdateModal from '../components/FlyerUpdate/FlyerUpdateModal'
import { useEventDetail } from '../hooks/useEventDetail'

import { API_BASE_URL } from '../config/api'

const EventDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [deleteError, setDeleteError] = useState('')
  const [flyerModalOpen, setFlyerModalOpen] = useState(false)
  const [flyerSuccess, setFlyerSuccess] = useState('')

  const {
    event,
    setEvent,
    loading,
    error,
    success,
    showUpload,
    artistEditModalOpen,
    editingArtistIndex,
    handleUploadSuccess,
    handleUploadError,
    handleMediaUpdate,
    openArtistEditModal,
    handleArtistSave,
    handleArtistToggle,
    assignArtistsToDay,
    handleEditEvent,
    toggleUpload,
    closeArtistModal
  } = useEventDetail(id, user)

  // The apply endpoint answers with the updated event, so the page refreshes
  // from the response rather than re-fetching.
  const handleFlyerApplied = (updatedEvent) => {
    setEvent(updatedEvent)
    setFlyerModalOpen(false)
    setFlyerSuccess(
      `Flyer applied — ${updatedEvent.appliedCount} ${updatedEvent.appliedCount === 1 ? 'change' : 'changes'} saved`
    )
    setTimeout(() => setFlyerSuccess(''), 5000)
  }

  const handleDeleteEvent = async () => {
    const confirmMessage = `Are you sure you want to delete "${event.name}"? This action cannot be undone.`
    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/events/${id}`, {
        withCredentials: true
      })
      navigate('/passport')
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error deleting event:', err)
      setDeleteError(err.response?.data?.message || 'Failed to delete event')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading event details..." />
  }

  if (error && !event) {
    return <ErrorMessage error={error} />
  }

  if (!event) {
    return null
  }

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <EventHeader
          event={event}
          user={user}
          onEditClick={handleEditEvent}
          onDeleteClick={handleDeleteEvent}
          onFlyerUpdateClick={() => setFlyerModalOpen(true)}
        />

        <StatusMessage type="success" message={success || flyerSuccess} />
        <StatusMessage type="error" message={error || deleteError} />

        <EventInfo
          event={event}
          user={user}
          onArtistToggle={handleArtistToggle}
          onCommentClick={openArtistEditModal}
          onAssignDay={assignArtistsToDay}
        />

        <SocialSection event={event} user={user} />

        <MediaSection
          event={event}
          user={user}
          showUpload={showUpload}
          onToggleUpload={toggleUpload}
          onUploadSuccess={handleUploadSuccess}
          onUploadError={handleUploadError}
          onMediaUpdate={handleMediaUpdate}
        />

        <ArtistEditModal
          isOpen={artistEditModalOpen}
          onClose={closeArtistModal}
          onSave={handleArtistSave}
          artist={editingArtistIndex !== null ? event.artistsSeen[editingArtistIndex] : null}
          eventDates={event?.additionalDates || []}
        />

        <FlyerUpdateModal
          isOpen={flyerModalOpen}
          eventId={id}
          onClose={() => setFlyerModalOpen(false)}
          onApplied={handleFlyerApplied}
        />
      </div>
    </div>
  )
}

const ErrorMessage = ({ error }) => {
  const navigate = useNavigate()

  return (
    <div className="p-8">
      <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-6 py-4 rounded-xl">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="font-medium">Error Loading Event</h3>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <button
          onClick={() => navigate('/home')}
          className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-all duration-300"
        >
          ← Back to Dashboard
        </button>
      </div>
    </div>
  )
}

export default EventDetail